import { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, Minus, Plus, Trash2, Loader2, 
  ShoppingBag, ArrowRight, Truck, Tag, ChevronRight, Shield, Package, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const navigate = useNavigate();
  const { user, refreshCart } = useOutletContext();
  
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/shop/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    setUpdatingItem(itemId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/shop/cart/${itemId}`, 
        { quantity: newQuantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
      refreshCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
    } finally {
      setUpdatingItem(null);
    }
  };

  const removeItem = async (itemId) => {
    setUpdatingItem(itemId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/shop/cart/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item removed');
      await fetchCart();
      refreshCart();
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setUpdatingItem(null);
    }
  };

  const clearCart = async () => {
    if (!confirm('Are you sure you want to clear your cart?')) return;
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/shop/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cart cleared');
      await fetchCart();
      refreshCart();
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    // Simulate promo code application
    setTimeout(() => {
      toast.info('Promo code feature coming soon!');
      setApplyingPromo(false);
    }, 1000);
  };

  const handleCheckout = () => {
    navigate('/shop/checkout');
  };

  if (!user) {
    return (
      <div className="shop-page min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Your Cart is Waiting</h2>
          <p className="text-gray-500 mb-6">Please login to view your cart and complete your purchase</p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            data-testid="login-btn"
          >
            Login to Continue
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
        <p className="text-amber-700">Loading your cart...</p>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="shop-page min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Your Cart is Empty</h2>
          <p className="text-gray-500 mb-6">Looks like you haven't added any items to your cart yet</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            data-testid="continue-shopping-btn"
          >
            Start Shopping
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-3 px-4">
        <div className="shop-container flex items-center gap-2 text-sm">
          <Link to="/shop" className="text-gray-500 hover:text-amber-600 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <span className="text-gray-800 font-medium">Shopping Cart</span>
        </div>
      </div>

      <div className="shop-container py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Shopping Cart
            <span className="text-amber-600 ml-2 text-xl">({cart.total_items} items)</span>
          </h1>
          <button
            onClick={clearCart}
            className="text-sm text-red-500 hover:text-red-600 hover:underline font-medium"
          >
            Clear Cart
          </button>
        </div>

        <div className="shop-checkout-layout">
          {/* Cart Items */}
          <div className="space-y-4">
            {cart.items.map(item => {
              const hasDiscount = item.product?.original_price && item.product?.original_price > item.product?.price;
              
              return (
                <div 
                  key={item.id}
                  className={`bg-white rounded-xl border border-gray-200 p-4 sm:p-5 ${
                    updatingItem === item.id ? 'opacity-50' : ''
                  }`}
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link 
                      to={`/shop/product/${item.product?.id}`}
                      className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 bg-gray-50 rounded-lg overflow-hidden border border-gray-100"
                    >
                      <img
                        src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=150'}
                        alt={item.product?.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/shop/product/${item.product?.id}`}
                        className="font-semibold text-gray-900 hover:text-amber-600 line-clamp-2 transition-colors"
                      >
                        {item.product?.name}
                      </Link>
                      <p className="text-sm text-amber-600 mt-1">{item.product?.category_name}</p>
                      
                      {/* Price */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-lg font-bold text-gray-900">₹{item.product?.price}</span>
                        {hasDiscount && (
                          <>
                            <span className="text-sm text-gray-400 line-through">
                              ₹{item.product?.original_price}
                            </span>
                            <span className="text-xs text-green-600 font-semibold bg-green-50 px-2 py-0.5 rounded">
                              ₹{item.product?.original_price - item.product?.price} OFF
                            </span>
                          </>
                        )}
                      </div>

                      {/* Mobile: Quantity & Remove */}
                      <div className="flex items-center justify-between mt-3 sm:hidden">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                            disabled={updatingItem === item.id}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            disabled={updatingItem === item.id || item.quantity >= (item.product?.stock || 10)}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={updatingItem === item.id}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Desktop: Quantity & Remove */}
                    <div className="hidden sm:flex flex-col items-end gap-3">
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={updatingItem === item.id}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>

                      <div className="flex items-center border border-gray-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                          disabled={updatingItem === item.id}
                          className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                          data-testid={`qty-minus-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={updatingItem === item.id || item.quantity >= (item.product?.stock || 10)}
                          className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                          data-testid={`qty-plus-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <p className="font-bold text-gray-900 text-lg">
                        ₹{(item.product?.price || 0) * item.quantity}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Continue Shopping */}
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-amber-600 font-medium hover:text-amber-700 mt-4"
            >
              <ShoppingBag className="w-4 h-4" />
              Continue Shopping
            </Link>
          </div>

          {/* Order Summary Sidebar */}
          <div className="shop-checkout-sidebar">
            <div className="shop-order-summary">
              <h2 className="shop-order-summary-title">Order Summary</h2>
              
              {/* Price Details */}
              <div className="shop-order-totals">
                <div className="shop-order-total-row">
                  <span>Subtotal ({cart.total_items} items)</span>
                  <span className="font-medium text-gray-900">₹{cart.subtotal.toFixed(2)}</span>
                </div>
                <div className="shop-order-total-row">
                  <span className="flex items-center gap-1.5">
                    <Truck className="w-4 h-4 text-amber-500" />
                    Delivery
                  </span>
                  {cart.delivery_fee === 0 ? (
                    <span className="text-green-600 font-semibold">FREE</span>
                  ) : (
                    <span>₹{cart.delivery_fee}</span>
                  )}
                </div>
                {cart.subtotal < 500 && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-100 mt-2">
                    <span className="font-semibold">Add ₹{(500 - cart.subtotal).toFixed(0)} more</span> for free delivery!
                  </div>
                )}

                <div className="shop-order-total-row final">
                  <span>Total</span>
                  <span>₹{cart.total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Including all taxes</p>
              </div>

              {/* Promo Code */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Tag className="w-4 h-4 text-amber-500" />
                  <span className="font-medium">Have a promo code?</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300"
                    data-testid="promo-input"
                  />
                  <button 
                    onClick={handleApplyPromo}
                    disabled={applyingPromo}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </button>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="shop-place-order-btn flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                <Lock className="w-4 h-4" />
                Proceed to Checkout
              </button>

              {/* Trust Section */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-green-500" />
                    Secure Checkout
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4 text-amber-500" />
                    Sacred Packaging
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
